import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useRuleGradeClass, useRunGradingMonth } from '../services/conversionsApi';
import type { GradeLedgerView, RulingKind } from '../types';
import { formatLocalDateTime } from '../lib/provenance';
import { CalendarDateInput } from './dates/CalendarDateInput';
import { MISS_CLASSES, RULING_KINDS, recentMonths } from '../lib/gradeLedger';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heading: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold },
  sub: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'end', flexWrap: 'wrap' },
  list: { margin: 0, paddingLeft: tokens.spacingHorizontalXL },
});

/**
 * Accuracy §2 on the board, ADMIN ONLY (the server sends `ledger` to an admin alone, and every write is admin-gated):
 * grade a month and store it, rule a miss CLASS, and read the watermark, the unruled misses and the regressions.
 */
export default function GradeLedgerSection({ ledger, now = new Date() }: { ledger: GradeLedgerView; now?: Date }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const months = recentMonths(now);
  // The month just ended is the one usually graded.
  const [month, setMonth] = useState(months[1]);
  const [missClass, setMissClass] = useState<string>(MISS_CLASSES[0]);
  const [kind, setKind] = useState<RulingKind>('accepted');
  const [fixDate, setFixDate] = useState('');
  const [note, setNote] = useState('');
  const run = useRunGradingMonth();
  const rule = useRuleGradeClass();
  const when = (iso?: string | null) => (iso ? formatLocalDateTime(iso, i18n.language) : '—');
  const className = (c: string) => t(`accuracyPage.byClient.missClass.${c}`, { defaultValue: c });
  const kindName = (k: string) => t(`accuracyPage.ledger.kinds.${k}`, { defaultValue: k });
  const needsFix = kind === 'parser-fix';
  const unruled = Object.entries(ledger.unruled);

  return (
    <section className={styles.section} aria-labelledby="grade-ledger-heading">
      <Text as="h2" id="grade-ledger-heading" className={styles.heading}>{t('accuracyPage.ledger.heading')}</Text>
      <Text className={styles.muted}>{t('accuracyPage.ledger.intro')}</Text>

      <Text as="h3" className={styles.sub}>{t('accuracyPage.ledger.runHeading')}</Text>
      <Text>
        {ledger.watermark
          ? t('accuracyPage.ledger.watermark', {
              month: ledger.watermark.month,
              through: when(ledger.watermark.coveredThrough),
              count: ledger.watermark.graded,
              misses: ledger.watermark.misses,
            })
          : t('accuracyPage.ledger.noRun')}
      </Text>
      <div className={styles.row}>
        <Field label={t('accuracyPage.ledger.month')}>
          <Select value={month} onChange={(_e, data) => setMonth(data.value)}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </Field>
        <Button
          appearance="primary"
          disabled={run.isPending}
          icon={run.isPending ? <Spinner size="tiny" /> : undefined}
          onClick={() => run.mutate(month)}
        >
          {t('accuracyPage.ledger.run')}
        </Button>
      </div>
      {run.data && (
        <MessageBar intent="success">
          <MessageBarBody>
            {t('accuracyPage.ledger.ran', { month: run.data.month, count: run.data.graded, misses: run.data.misses, superseded: run.data.superseded })}
          </MessageBarBody>
        </MessageBar>
      )}
      {run.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{run.error instanceof Error ? run.error.message : t('common.unknownError')}</MessageBarBody>
        </MessageBar>
      )}

      <Text as="h3" className={styles.sub}>{t('accuracyPage.ledger.unruledHeading')}</Text>
      {unruled.length === 0 ? (
        <Text className={styles.muted}>{t('accuracyPage.ledger.noUnruled')}</Text>
      ) : (
        <ul className={styles.list}>
          {unruled.map(([c, n]) => <li key={c}>{t('accuracyPage.ledger.unruledLine', { miss: className(c), count: n })}</li>)}
        </ul>
      )}

      <Text as="h3" className={styles.sub}>{t('accuracyPage.ledger.ruleHeading')}</Text>
      <div className={styles.row}>
        <Field label={t('accuracyPage.ledger.missClass')}>
          <Select value={missClass} onChange={(_e, data) => setMissClass(data.value)}>
            {MISS_CLASSES.map((c) => <option key={c} value={c}>{className(c)}</option>)}
          </Select>
        </Field>
        <Field label={t('accuracyPage.ledger.kind')}>
          <Select value={kind} onChange={(_e, data) => setKind(data.value as RulingKind)}>
            {RULING_KINDS.map((k) => <option key={k} value={k}>{kindName(k)}</option>)}
          </Select>
        </Field>
        {needsFix && (
          <Field label={t('accuracyPage.ledger.fixDeployed')}>
            <CalendarDateInput value={fixDate} onChange={setFixDate} />
          </Field>
        )}
        <Field label={t('accuracyPage.ledger.note')}>
          <Input value={note} maxLength={500} onChange={(_e, data) => setNote(data.value)} />
        </Field>
        <Button
          disabled={rule.isPending || (needsFix && !fixDate)}
          onClick={() =>
            rule.mutate(
              { missClass, kind, fixDeployedAt: needsFix ? `${fixDate}T00:00:00Z` : null, note: note.trim() || null },
              { onSuccess: () => setNote('') },
            )}
        >
          {t('accuracyPage.ledger.rule')}
        </Button>
      </div>
      {rule.data && (
        <MessageBar intent="success">
          <MessageBarBody>{t('accuracyPage.ledger.ruled', { miss: className(rule.data.missClass), count: rule.data.applied })}</MessageBarBody>
        </MessageBar>
      )}
      {rule.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{rule.error instanceof Error ? rule.error.message : t('common.unknownError')}</MessageBarBody>
        </MessageBar>
      )}

      <Text as="h3" className={styles.sub}>{t('accuracyPage.ledger.regressionsHeading')}</Text>
      {ledger.regressions.length === 0 ? (
        <Text className={styles.muted}>{t('accuracyPage.ledger.noRegressions')}</Text>
      ) : (
        <ul className={styles.list} aria-label={t('accuracyPage.ledger.regressionsHeading')}>
          {ledger.regressions.map((g) => (
            <li key={g.id}>{t('accuracyPage.ledger.regressionLine', { miss: className(g.missClass ?? ''), journal: g.journalId, date: when(g.exportedAt) })}</li>
          ))}
        </ul>
      )}

      <Text as="h3" className={styles.sub}>{t('accuracyPage.ledger.rulingsHeading')}</Text>
      {ledger.rulings.length === 0 ? (
        <Text className={styles.muted}>{t('accuracyPage.ledger.noRulings')}</Text>
      ) : (
        <ul className={styles.list} aria-label={t('accuracyPage.ledger.rulingsHeading')}>
          {ledger.rulings.map((r) => (
            <li key={r.id}>
              {t('accuracyPage.ledger.rulingLine', {
                miss: className(r.missClass), kind: kindName(r.kind), who: r.ruledByName ?? t('accuracyPage.ledger.someone'),
                date: when(r.ruledAt), count: r.applied,
              })}
              {r.note ? ` · ${r.note}` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
